import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import TablePagination from '@mui/material/TablePagination';
import InputAdornment from '@mui/material/InputAdornment';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import ClearIcon from '@mui/icons-material/Clear';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';

import {
  AdminAnnouncementResponse,
  AdminAnnouncementCreateRequest,
  AdminAnnouncementUpdateRequest,
  AnnouncementCategory,
  AdminStoreList,
  AnnouncementFormData
} from '../../types';
import api from '../../services/api';

const AnnouncementManagementTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 基本状態
  const [announcements, setAnnouncements] = useState<AdminAnnouncementResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // カテゴリと店舗データ
  const [categories, setCategories] = useState<AnnouncementCategory[]>([]);
  const [stores, setStores] = useState<AdminStoreList[]>([]);

  // ダイアログ状態
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<AdminAnnouncementResponse | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<AdminAnnouncementResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<AdminAnnouncementResponse | null>(null);

  // フォームデータ
  const [formData, setFormData] = useState<AnnouncementFormData>({
    title: '',
    content: '',
    category_id: '',
    priority: 0,
    start_date: new Date(),
    end_date: null,
    is_published: false,
    is_pinned: false,
    target_store_ids: []
  });

  // 送信状態
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // DatePicker開閉状態
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  // 日付フォーマット用のヘルパー関数
  const formatDateTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // JST時間を文字列として送信するためのヘルパー関数
  const formatDateTimeForAPI = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  // データ取得をuseCallbackでメモ化
  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.adminAnnouncements.getAll({
        page: page + 1,
        per_page: rowsPerPage
      });
      setAnnouncements(response.announcements);
      setTotal(response.total);
    } catch (err: any) {
      setError('お知らせの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  const fetchCategories = async () => {
    try {
      const response = await api.adminAnnouncements.getCategories();
      setCategories(response);
    } catch (err: any) {
    }
  };

  const fetchStores = async () => {
    try {
      const response = await api.adminStore.getAll();
      setStores(response);
    } catch (err: any) {
    }
  };

  // データ取得
  useEffect(() => {
    fetchAnnouncements();
    fetchCategories();
    fetchStores();
  }, [fetchAnnouncements]);

  // ページネーション
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ダイアログ制御
  const handleEditClick = (announcement?: AdminAnnouncementResponse) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        title: announcement.title,
        content: announcement.content,
        category_id: announcement.category_id,
        priority: announcement.priority,
        start_date: new Date(announcement.start_date),
        end_date: announcement.end_date ? new Date(announcement.end_date) : null,
        is_published: announcement.is_published,
        is_pinned: announcement.is_pinned,
        target_store_ids: announcement.target_store_ids
      });
    } else {
      setEditingAnnouncement(null);
      setFormData({
        title: '',
        content: '',
        category_id: '',
        priority: 0,
        start_date: new Date(),
        end_date: null,
        is_published: false,
        is_pinned: false,
        target_store_ids: []
      });
    }
    setSubmitAttempted(false);
    setError(null);
    setStartDatePickerOpen(false);
    setEndDatePickerOpen(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAnnouncement(null);
    setError(null);
    setStartDatePickerOpen(false);
    setEndDatePickerOpen(false);
  };

  const handleViewDetails = async (announcement: AdminAnnouncementResponse) => {
    try {
      const detail = await api.adminAnnouncements.getById(announcement.id);
      setSelectedAnnouncement(detail);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setError('お知らせ詳細の取得に失敗しました');
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedAnnouncement(null);
  };

  // フォーム入力
  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const handleDateChange = (field: string) => (date: Date | null) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  // 保存（JST時間をそのまま送信するように修正）
  const handleSave = async () => {
    setSubmitAttempted(true);

    if (!isFormValid()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const requestData: AdminAnnouncementCreateRequest | AdminAnnouncementUpdateRequest = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        category_id: formData.category_id,
        priority: formData.priority,
        start_date: formData.start_date ? formatDateTimeForAPI(formData.start_date) : formatDateTimeForAPI(new Date()),
        end_date: formData.end_date ? formatDateTimeForAPI(formData.end_date) : undefined,
        target_type: 'all',
        is_published: formData.is_published,
        is_pinned: formData.is_pinned,
        target_store_ids: formData.target_store_ids
      };

      if (editingAnnouncement) {
        await api.adminAnnouncements.update(editingAnnouncement.id, requestData);
      } else {
        await api.adminAnnouncements.create(requestData as AdminAnnouncementCreateRequest);
      }

      handleCloseDialog();
      fetchAnnouncements();
    } catch (err: any) {
      setError(editingAnnouncement ? 'お知らせの更新に失敗しました' : 'お知らせの作成に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // 削除
  const handleDelete = async () => {
    if (!announcementToDelete) return;

    setIsSaving(true);
    try {
      await api.adminAnnouncements.delete(announcementToDelete.id);
      setDeleteDialogOpen(false);
      setAnnouncementToDelete(null);
      fetchAnnouncements();
    } catch (err: any) {
      setError('お知らせの削除に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // バリデーション
  const isFormValid = () => {
    return formData.title.trim() && 
           formData.content.trim() && 
           formData.category_id &&
           (formData.start_date || true); // start_dateが未設定の場合は現在時刻を使用
  };

  // ステータス表示
  const getStatusChip = (announcement: AdminAnnouncementResponse) => {
    const now = new Date();
    const startDate = new Date(announcement.start_date);
    const endDate = announcement.end_date ? new Date(announcement.end_date) : null;

    if (!announcement.is_published) {
      return <Chip label="下書き" size="small" color="default" />;
    }
    
    if (now < startDate) {
      return <Chip label="配信予約" size="small" color="info" />;
    }
    
    if (endDate && now > endDate) {
      return <Chip label="配信終了" size="small" color="error" />;
    }
    
    return <Chip label="配信中" size="small" color="success" />;
  };

  // モバイル用カード表示
  const renderMobileCard = (announcement: AdminAnnouncementResponse) => (
    <Card key={announcement.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
              {announcement.is_pinned && (
                <PushPinIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
              )}
              {announcement.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {getStatusChip(announcement)}
              <Chip 
                label={announcement.category_name || 'カテゴリなし'} 
                size="small" 
                variant="outlined" 
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            配信開始日時
          </Typography>
          <Typography variant="body2">
            {formatDateTime(announcement.start_date)}
          </Typography>
        </Box>

        {announcement.end_date && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              配信終了日時
            </Typography>
            <Typography variant="body2">
              {formatDateTime(announcement.end_date)}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            対象店舗
          </Typography>
          <Typography variant="body2">
            {announcement.target_store_ids.length === 0 
              ? '全店舗' 
              : `${announcement.target_store_names.length}店舗`
            }
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDetails(announcement)}
          >
            詳細
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEditClick(announcement)}
          >
            編集
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setAnnouncementToDelete(announcement);
              setDeleteDialogOpen(true);
            }}
            color="error"
          >
            削除
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <div>
            <Typography variant="h5" gutterBottom>
              店舗向けお知らせ管理
            </Typography>
            <Typography variant="body1" color="text.secondary">
              店舗に配信するお知らせの作成・編集・配信を行います。
            </Typography>
          </div>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleEditClick()}
            sx={{ minWidth: 140 }}
          >
            新規お知らせ作成
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isMobile ? (
              // モバイル表示: カード
              announcements.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    お知らせがありません
                  </Typography>
                </Box>
              ) : (
                announcements.map(announcement => renderMobileCard(announcement))
              )
            ) : (
              // PC表示: テーブル
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>タイトル</TableCell>
                        <TableCell>カテゴリ</TableCell>
                        <TableCell>ステータス</TableCell>
                        <TableCell>配信期間</TableCell>
                        <TableCell>対象店舗</TableCell>
                        <TableCell align="center">操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {announcements.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body1" color="text.secondary">
                              お知らせがありません
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        announcements.map(announcement => (
                          <TableRow key={announcement.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {announcement.is_pinned && (
                                  <PushPinIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                                )}
                                <Typography variant="body2">
                                  {announcement.title}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={announcement.category_name || 'カテゴリなし'} 
                                size="small" 
                                variant="outlined" 
                              />
                            </TableCell>
                            <TableCell>
                              {getStatusChip(announcement)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(announcement.start_date)}
                                {announcement.end_date && (
                                  <>
                                    <br />
                                    〜 {formatDate(announcement.end_date)}
                                  </>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {announcement.target_store_ids.length === 0 
                                  ? '全店舗' 
                                  : `${announcement.target_store_names.length}店舗`
                                }
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(announcement)}
                                  color="info"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(announcement)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setAnnouncementToDelete(announcement);
                                    setDeleteDialogOpen(true);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="表示件数:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
                />
              </Paper>
            )}
          </>
        )}

        {/* 詳細表示ダイアログ */}
        <Dialog
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: isMobile
              ? {
                  m: 1.5,
                  width: 'calc(100% - 24px)',
                  maxWidth: 'calc(100% - 24px)',
                  maxHeight: 'calc(100dvh - 24px)',
                  borderRadius: 2,
                }
              : {},
          }}
        >
          <DialogTitle>
            お知らせ詳細
            <IconButton
              aria-label="close"
              onClick={handleCloseDetailDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedAnnouncement && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedAnnouncement.is_pinned && (
                      <PushPinIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                    )}
                    {selectedAnnouncement.title}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {getStatusChip(selectedAnnouncement)}
                    <Chip 
                      label={selectedAnnouncement.category_name || 'カテゴリなし'} 
                      size="small" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`優先度: ${selectedAnnouncement.priority}`} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      内容
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                      {selectedAnnouncement.content}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        配信開始日時
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(selectedAnnouncement.start_date)}
                      </Typography>
                    </Box>
                    
                    {selectedAnnouncement.end_date && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          配信終了日時
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(selectedAnnouncement.end_date)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      対象店舗
                    </Typography>
                    <Typography variant="body1">
                      {selectedAnnouncement.target_store_ids.length === 0 
                        ? '全店舗' 
                        : selectedAnnouncement.target_store_names.join(', ')
                      }
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        作成日時
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(selectedAnnouncement.created_at)}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        更新日時
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(selectedAnnouncement.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailDialog} color="primary">
              閉じる
            </Button>
          </DialogActions>
        </Dialog>

        {/* 新規作成・編集ダイアログ */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: isMobile
              ? {
                  m: 1.5,
                  width: 'calc(100% - 24px)',
                  maxWidth: 'calc(100% - 24px)',
                  maxHeight: 'calc(100dvh - 24px)',
                  borderRadius: 2,
                }
              : {},
          }}
        >
          <DialogTitle>
            {editingAnnouncement ? 'お知らせ編集' : '新規お知らせ作成'}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
            <Box sx={{ pt: 1 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="タイトル"
                value={formData.title}
                onChange={handleInputChange('title')}
                margin="normal"
                required
                error={submitAttempted && !formData.title.trim()}
                helperText={submitAttempted && !formData.title.trim() ? 'タイトルは必須です' : ''}
              />

              <TextField
                fullWidth
                label="内容"
                value={formData.content}
                onChange={handleInputChange('content')}
                margin="normal"
                required
                multiline
                rows={4}
                error={submitAttempted && !formData.content.trim()}
                helperText={submitAttempted && !formData.content.trim() ? '内容は必須です' : ''}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>カテゴリ</InputLabel>
                  <Select
                    value={formData.category_id}
                    onChange={handleSelectChange('category_id')}
                    label="カテゴリ"
                    error={submitAttempted && !formData.category_id}
                  >
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="優先度"
                  type="number"
                  value={formData.priority}
                  onChange={handleInputChange('priority')}
                  inputProps={{ min: 0, max: 10 }}
                  sx={{ minWidth: 120 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {/* 配信開始日時 */}
                {formData.start_date === null ? (
                  <TextField
                    fullWidth
                    label="配信開始日時"
                    placeholder="未設定（現在時刻で保存されます）"
                    value=""
                    onClick={() => {
                      const now = new Date();
                      setFormData(prev => ({ ...prev, start_date: now }));
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ) : (
                  <DateTimePicker
                    label="配信開始日時"
                    value={formData.start_date}
                    onChange={handleDateChange('start_date')}
                    format="yyyy/MM/dd HH:mm"
                    open={startDatePickerOpen}
                    onOpen={() => setStartDatePickerOpen(true)}
                    onClose={() => setStartDatePickerOpen(false)}
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onClick: () => setStartDatePickerOpen(true),
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setFormData(prev => ({ ...prev, start_date: null }));
                                  setStartDatePickerOpen(false);
                                }}
                                edge="end"
                                tabIndex={-1}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      },
                      actionBar: {
                        actions: ['clear', 'cancel', 'accept']
                      },
                      digitalClockSectionItem: {
                        sx: {
                          fontSize: '0.875rem'
                        }
                      }
                    }}
                  />
                )}

                {/* 配信終了日時 */}
                {formData.end_date === null ? (
                  <TextField
                    fullWidth
                    label="配信終了日時"
                    placeholder="未設定"
                    value=""
                    onClick={() => {
                      // 開始日時の1時間後を設定
                      const endDate = formData.start_date ? new Date(formData.start_date.getTime() + 60 * 60 * 1000) : new Date();
                      setFormData(prev => ({ ...prev, end_date: endDate }));
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ) : (
                  <DateTimePicker
                    label="配信終了日時"
                    value={formData.end_date}
                    onChange={handleDateChange('end_date')}
                    format="yyyy/MM/dd HH:mm"
                    open={endDatePickerOpen}
                    onOpen={() => setEndDatePickerOpen(true)}
                    onClose={() => setEndDatePickerOpen(false)}
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onClick: () => setEndDatePickerOpen(true),
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setFormData(prev => ({ ...prev, end_date: null }));
                                  setEndDatePickerOpen(false);
                                }}
                                edge="end"
                                tabIndex={-1}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      },
                      actionBar: {
                        actions: ['clear', 'cancel', 'accept']
                      },
                      digitalClockSectionItem: {
                        sx: {
                          fontSize: '0.875rem'
                        }
                      }
                    }}
                  />
                )}
              </Box>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>対象店舗</InputLabel>
                <Select
                  multiple
                  value={formData.target_store_ids}
                  onChange={handleSelectChange('target_store_ids')}
                  input={<OutlinedInput label="対象店舗" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return '全店舗';
                    const names = stores
                      .filter(store => selected.includes(store.id))
                      .map(store => store.name);
                    return names.join(', ');
                  }}
                >
                  {stores.map(store => (
                    <MenuItem key={store.id} value={store.id}>
                      <Checkbox checked={formData.target_store_ids.includes(store.id)} />
                      <ListItemText primary={store.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_published}
                      onChange={handleSwitchChange('is_published')}
                    />
                  }
                  label="公開する"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_pinned}
                      onChange={handleSwitchChange('is_pinned')}
                    />
                  }
                  label="ピン留めする"
                  sx={{ ml: 2 }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              キャンセル
            </Button>
            <Button 
              onClick={handleSave}
              variant="contained"
              disabled={isSaving}
            >
              {isSaving ? '保存中...' : (editingAnnouncement ? '更新' : '作成')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>
            お知らせ削除確認
            <IconButton
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              以下のお知らせを削除してもよろしいですか？
            </DialogContentText>
            {announcementToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">タイトル</Typography>
                <Typography variant="body1">{announcementToDelete.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>ステータス</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {getStatusChip(announcementToDelete)}
                </Box>
              </Box>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              この操作は元に戻せません。削除されたお知らせは復元できません。
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={handleDelete}
              color="error" 
              variant="contained"
              disabled={isSaving}
            >
              {isSaving ? '削除中...' : '削除'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </LocalizationProvider>
  );
};

export default AnnouncementManagementTab;